import React, { useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { PageHeaderDrawer } from '../page-header-drawer'
import { ChargeAmpsLandingPageLogo } from '../charge-amps-landing-page-logo'

const Container = styled.header<{ scrolled: boolean }>`
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:after {
        position: absolute;
        content: '';
        right: 0;
        left: 0;
        bottom: 0;
        height: 1px;
        background-color: ${({ theme, scrolled }) => (scrolled ? theme.brandColors.CA_Light_Gray : 'transparent')};
        transition: background-color 0.2s;
    }
`
export const Link = styled.a.withConfig({ shouldForwardProp: (p) => p !== 'light' && p !== 'selected' })<{
    selected?: boolean
    light?: boolean
}>`
    color: ${({ theme, selected, light }) => {
        if (selected) return theme.brandColors.CA_Green
        if (light) return theme.font.lightestColor

        return theme.font.midDarkColor
    }};
    transition: all 0.2s ease;
    letter-spacing: 0.01em;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: ${({ selected }) => (selected ? 'none' : 'underline')};
    }

    &:focus {
        text-decoration: none;
        color: ${({ theme, selected }) => (selected ? theme.font.midLightColor : theme.font.midDarkColor)};
    }
`

export type LinkType = {
    labelCode: string
    href?: string
    displayLabelCode?: string
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
    prefetch?: () => void
    subMenu?: LinkType[]
    hasNotification?: boolean
}

export type PageHeaderProps = {
    links: LinkType[]
    className?: ClassName
    style?: React.CSSProperties
    title?: string
    disableLogout?: boolean
    logoutDisabledReason?: string
}

export function PageHeader({
    className,
    style,
    links,
    title,
    disableLogout,
    logoutDisabledReason,
}: PageHeaderProps): React.ReactElement {
    const [scrolled, setScrolled] = useState(false)

    const { pathname } = useLocation()

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY !== 0)

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    })

    return (
        <Container className={cn(className)} style={style} scrolled={scrolled} data-testid="header-container">
            <div className="flex gap-3 md:gap-4 align-items-center">
                <div data-testid="caLogo">
                    <RouterLink
                        to={'/dashboard'}
                        onClick={() => {
                            if (pathname === 'dashboard') window.scrollTo(0, 0)
                        }}
                    >
                        <ChargeAmpsLandingPageLogo />
                    </RouterLink>
                </div>

                {title ? (
                    <h1 className="m-0 mb-1 line-height-1 text-xl md:text-2xl" data-testid="header-title">
                        {title}
                    </h1>
                ) : null}
            </div>

            <PageHeaderDrawer links={links} disableLogout={disableLogout} logoutDisabledReason={logoutDisabledReason} />
        </Container>
    )
}
